// Phase 9.5 — F&B Dashboard: location-health tiles + compact upload strip.
//
// Layout (top → bottom):
//   Row 1 — Compact Upload Strip: primary "Upload today's stock" +
//           last-uploaded chip (staleness + uploader email)
//   Row 2 — Location Health Grid: one tile per active outlet with stock,
//           colored by Q1 health thresholds. Click to drill into
//           /tools/fnb-po/location/:id
//   Row 3 — Bar Stock link (monthly)
//   Row 4 — Master Data mini-cards (unchanged from 9.4)
//   Row 5 — Raw ingestion previews collapsed at the very bottom
//
// EVERYTHING else from the pre-9.5 landing has been removed per user
// spec: no metrics tiles, no reorder-overview chip grid on the
// dashboard (it lives on the reorder page).

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

// ---- Utils ----------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function stalenessLabel(hours) {
  if (hours === null || hours === undefined) return { tone: "rose", text: "Never uploaded" };
  if (hours < 1)  return { tone: "emerald", text: "Fresh · uploaded less than an hour ago" };
  if (hours < 24) {
    const h = Math.round(hours);
    return { tone: "emerald", text: `Fresh · ${h} hour${h === 1 ? "" : "s"} ago` };
  }
  const d = Math.round(hours / 24);
  if (hours < 72) return { tone: "amber", text: `Aging · ${d} day${d === 1 ? "" : "s"} ago` };
  return { tone: "rose", text: `Needs refresh · ${d} days ago` };
}

const HEALTH_COLORS = {
  green:  { bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-800" },
  yellow: { bg: "bg-amber-50",   border: "border-amber-200",   dot: "bg-amber-500",   text: "text-amber-800" },
  red:    { bg: "bg-rose-50",    border: "border-rose-200",    dot: "bg-rose-500",    text: "text-rose-800" },
};

const TONE_STYLES = {
  emerald: "bg-emerald-500/10 text-emerald-800 border-emerald-500/40",
  amber:   "bg-amber-500/10   text-amber-800   border-amber-500/40",
  rose:    "bg-rose-500/10    text-rose-800    border-rose-500/40",
};

// ---- Compact Upload Strip -------------------------------------------------

function UploadStrip({ statusEntry, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const s = stalenessLabel(statusEntry?.staleness_hours);
  const toneCls = TONE_STYLES[s.tone];

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/fnb/uploads/stock-balance", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 5 * 60 * 1000,
      });
      if (res.data?.duplicate_upload) {
        toast.warning("Duplicate file — no new rows. Use maintenance page to force re-import.");
      } else {
        toast.success(`Ingested ${res.data?.row_count?.toLocaleString?.() ?? "0"} rows`);
      }
      onUploaded?.();
    } catch (e) {
      setError(detailFromError(e));
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  }, [onUploaded]);

  return (
    <div className="space-y-2" data-testid="upload-strip">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) doUpload(f);
              e.target.value = "";
            }}
            disabled={busy}
            data-testid="upload-strip-input"
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="bg-brand hover:bg-brand-hover text-white shadow-sm"
            data-testid="upload-strip-btn"
          >
            {busy ? `Uploading… ${progress}%` : "Upload today’s stock"}
          </Button>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneCls}`}
            data-testid="upload-strip-staleness"
          >
            {s.text}
            {statusEntry?.last_uploaded_by ? (
              <span className="opacity-60">· by {statusEntry.last_uploaded_by}</span>
            ) : null}
          </span>
        </div>
        <Link
          to="/tools/fnb-po/master-data"
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
          data-testid="upload-strip-master-data-link"
        >
          Master data →
        </Link>
      </div>
      {busy && <Progress value={progress} />}
      {error && !busy && (
        <Alert variant="destructive" data-testid="upload-strip-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// ---- Location Health Grid -------------------------------------------------

function LocationTile({ tile, onToggleActive, saving }) {
  const c = HEALTH_COLORS[tile.health] || HEALTH_COLORS.green;
  const isInactive = tile.is_active === false;
  const muted = isInactive ? "opacity-50" : "";
  const isSaving = saving === tile.location_id;
  return (
    <div
      className={`relative rounded-lg border ${isInactive ? "border-slate-300 bg-slate-50" : `${c.border} ${c.bg}`} px-4 py-3 hover:shadow-sm transition-opacity ${muted}`}
      data-testid={`location-tile-${tile.location_id}`}
    >
      {/* Phase 9.6.1 UX — inline toggle replaces the "⋯" kebab menu.
          Always-visible switch; ON=active, OFF=inactive. Clicking
          calls onToggleActive which does optimistic UI + PATCH. */}
      <div
        className="absolute top-2 right-2 flex items-center gap-1.5 z-10"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <span
          className={`text-[9px] uppercase tracking-wide font-medium ${isInactive ? "text-slate-400" : "text-emerald-700"}`}
          data-testid={`tile-toggle-label-${tile.location_id}`}
        >
          {isInactive ? "Off" : "Active"}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={!isInactive}
          disabled={isSaving}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleActive(tile);
          }}
          title={isInactive ? "Mark unit active" : "Mark unit inactive"}
          className={`h-4 w-7 rounded-full transition-colors relative disabled:opacity-50 ${
            isInactive ? "bg-slate-300" : "bg-emerald-600"
          }`}
          data-testid={`tile-active-toggle-${tile.location_id}`}
        >
          <span
            className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
              isInactive ? "left-0.5" : "left-3.5"
            }`}
          />
        </button>
      </div>
      <Link
        to={`/tools/fnb-po/location/${tile.location_id}`}
        className="block"
      >
        <div className="flex items-start justify-between gap-3 pr-16">
          <div className="min-w-0">
            <div className={`font-medium text-sm ${isInactive ? "text-slate-500" : c.text} truncate`} data-testid={`tile-name-${tile.location_id}`}>
              {tile.display_name}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <span>Score {tile.health_score} · {tile.items_total} items</span>
              {isInactive ? (
                <span className="px-1.5 py-0.5 rounded bg-slate-200 text-[9px] uppercase tracking-wide text-slate-700"
                      data-testid={`tile-badge-inactive-${tile.location_id}`}>
                  Inactive
                </span>
              ) : tile.is_warehouse ? (
                <span
                  className="px-1.5 py-0.5 rounded bg-muted/60 text-[9px] uppercase tracking-wide text-muted-foreground"
                  data-testid={`tile-badge-warehouse-${tile.location_id}`}
                >
                  Warehouse
                </span>
              ) : tile.is_po_target === false ? (
                <span
                  className="px-1.5 py-0.5 rounded bg-muted/60 text-[9px] uppercase tracking-wide text-muted-foreground"
                  data-testid={`tile-badge-non-po-${tile.location_id}`}
                  title="Not part of the Pan-India PO template"
                >
                  Non-PO
                </span>
              ) : null}
            </div>
          </div>
          <span
            className={`h-2.5 w-2.5 rounded-full ${isInactive ? "bg-slate-400" : c.dot} mt-1.5 shrink-0`}
            data-testid={`tile-dot-${tile.location_id}`}
            aria-label={isInactive ? "inactive" : tile.health}
          />
        </div>
        <div className="mt-3 space-y-1 text-[12px]">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">OK</span>
            <span className="font-medium">{tile.items_ok}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Need reorder</span>
            <span className="font-medium">{tile.items_reorder_or_danger}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Out of stock</span>
            <span className={`font-semibold ${tile.items_out_of_stock > 0 && !isInactive ? c.text : ""}`}>
              {tile.items_out_of_stock}
            </span>
          </div>
          {tile.items_unconfigured > 0 && (
            <div
              className="flex items-center justify-between text-muted-foreground"
              data-testid={`tile-unconfigured-${tile.location_id}`}
            >
              <span>Unconfigured</span>
              <span className="font-medium">{tile.items_unconfigured}</span>
            </div>
          )}
        </div>
        {(tile.items_manual_excluded > 0 || tile.items_inactive > 0 || tile.items_inactive_here > 0 || tile.items_slow_moving > 0) && (
          <div className="mt-2 pt-2 border-t border-black/5 text-[10px] text-muted-foreground space-y-0.5">
            {tile.items_manual_excluded > 0 && (
              <div data-testid={`tile-manual-excluded-${tile.location_id}`}>
                <span className="text-emerald-800 font-medium">{tile.items_manual_excluded}</span> manually excluded
              </div>
            )}
            {tile.items_slow_moving > 0 && (
              <div data-testid={`tile-slow-moving-${tile.location_id}`}>
                <span className="text-indigo-700 font-medium">{tile.items_slow_moving}</span> slow-moving here
              </div>
            )}
            {tile.items_inactive_here > 0 && (
              <div data-testid={`tile-inactive-here-${tile.location_id}`}>
                {tile.items_inactive_here} items inactive here
              </div>
            )}
            {tile.items_inactive > 0 && (
              <div>{tile.items_inactive} inactive items excluded</div>
            )}
          </div>
        )}
      </Link>
    </div>
  );
}

function LocationHealthGrid({ tiles, loading, onToggleActive, saving }) {
  if (loading && !tiles) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="tiles-loading">
        Loading location tiles…
      </div>
    );
  }
  if (!tiles || tiles.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic" data-testid="tiles-empty">
        No active outlets found. Add one on the Locations page.
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      data-testid="location-tiles-grid"
    >
      {tiles.map((t) => (
        <LocationTile
          key={t.location_id}
          tile={t}
          onToggleActive={onToggleActive}
          saving={saving}
        />
      ))}
    </div>
  );
}

// ---- Master Data mini-cards (retained from 9.4) --------------------------

function MasterDataMiniCard({ entry, meta }) {
  const s = stalenessLabel(entry?.staleness_hours);
  return (
    <div
      className="rounded-lg border border-border/60 bg-background/40 px-4 py-3 space-y-2"
      data-testid={`master-data-card-${meta.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-foreground/90">{meta.label}</div>
          <div className="text-[11px] text-muted-foreground">
            {entry?.has_data ? formatDate(entry.last_upload_at) : "Never uploaded"}
          </div>
        </div>
        <span className={`text-[10px] font-medium ${
          s.tone === "emerald" ? "text-emerald-700"
          : s.tone === "amber" ? "text-amber-700" : "text-rose-700"
        }`}>
          {s.text.split(" · ")[0]}
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {meta.key === "vendor_rates" && entry?.has_data && (
          <>
            {(entry?.extra?.items_upserted ?? 0).toLocaleString()} items ·{" "}
            {(entry?.row_count ?? 0).toLocaleString()} rates ·{" "}
            {(entry?.extra?.vendors_upserted ?? 0).toLocaleString()} vendors
          </>
        )}
        {meta.key === "sales" && entry?.has_data && (
          <>
            {(entry?.row_count ?? 0).toLocaleString()} sales rows ·{" "}
            {(entry?.extra?.distinct_menu_items ?? 0).toLocaleString()} items
          </>
        )}
        {meta.key === "bom" && entry?.has_data && (
          <>
            {(entry?.extra?.recipe_boms ?? 0).toLocaleString()} recipe BOMs ·{" "}
            {(entry?.extra?.total_ingredients ?? 0).toLocaleString()} ingredients ·{" "}
            {(entry?.extra?.distinct_pos_skus ?? 0).toLocaleString()} POS SKUs mapped
          </>
        )}
        {meta.key === "bom" && !entry?.has_data && (
          <span className="italic">No BOMs uploaded yet. Upload the recipe CSV on the Master Data page.</span>
        )}
      </div>
      <div>
        <Link
          to="/tools/fnb-po/master-data"
          className="text-[11px] text-foreground/70 hover:text-foreground underline underline-offset-4"
          data-testid={`master-data-refresh-${meta.key}`}
        >
          Refresh →
        </Link>
      </div>
    </div>
  );
}

// ---- Main -----------------------------------------------------------------

export default function FnbPoLanding() {
  const [status, setStatus] = useState(null);
  const [tiles, setTiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLocId, setSavingLocId] = useState(null);
  const [showInactiveUnits, setShowInactiveUnits] = useState(() => {
    try { return localStorage.getItem("fnb.landing.showInactiveUnits") === "1"; }
    catch (_e) { return false; }
  });

  useEffect(() => {
    try {
      localStorage.setItem("fnb.landing.showInactiveUnits", showInactiveUnits ? "1" : "0");
    } catch (_e) { /* ignore */ }
  }, [showInactiveUnits]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const tilesUrl = `/fnb/analytics/location-tiles?item_scope=daily${
        showInactiveUnits ? "&include_inactive_units=true" : ""
      }`;
      const [statusRes, tilesRes] = await Promise.all([
        api.get("/fnb/master-data/status"),
        api.get(tilesUrl),
      ]);
      setStatus(statusRes.data);
      setTiles(tilesRes.data?.tiles || []);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [showInactiveUnits]);

  useEffect(() => { reload(); }, [reload]);

  const onToggleActive = useCallback(async (tile) => {
    // Phase 9.6.1 UX — optimistic tile toggle. Same pattern as the
    // Location detail applicability switch: flip local state instantly
    // (removing the tile from the grid when "Show inactive units" is
    // off), fire PATCH, revert on failure.
    const nextIsActive = !(tile.is_active !== false);
    const locId = tile.location_id;
    setSavingLocId(locId);
    let snapshot = null;
    setTiles((prev) => {
      snapshot = prev;
      if (!prev) return prev;
      if (!nextIsActive && !showInactiveUnits) {
        // Turning OFF while "Show inactive" is unchecked → drop it.
        return prev.filter((t) => t.location_id !== locId);
      }
      return prev.map((t) =>
        t.location_id === locId
          ? { ...t, is_active: nextIsActive, active_source: "manual" }
          : t,
      );
    });
    try {
      await api.patch(
        `/fnb/locations/${encodeURIComponent(locId)}`,
        { is_active: nextIsActive },
      );
      toast.success(
        nextIsActive
          ? `${tile.display_name} marked active`
          : `${tile.display_name} marked inactive. Won't be reset by uploads.`,
      );
    } catch (e) {
      if (snapshot !== null) setTiles(snapshot);
      toast.error(detailFromError(e) || "Could not update unit state.");
    } finally {
      setSavingLocId(null);
    }
  }, [showInactiveUnits]);

  if (loading && !status) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="fnb-landing-loading">
        Loading dashboard…
      </div>
    );
  }
  if (!status) {
    return (
      <Alert variant="destructive" data-testid="fnb-landing-error">
        <AlertTitle>Couldn&apos;t load dashboard</AlertTitle>
        <AlertDescription>Try refreshing.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8" data-testid="fnb-po-landing">
      <UploadStrip statusEntry={status.stock_balance} onUploaded={reload} />

      <section className="space-y-3" data-testid="location-health-section">
        <div className="flex items-baseline justify-between">
          <div>
            <h2 className="text-sm font-medium uppercase tracking-wider text-foreground/80">
              Location Health · Daily Stock
            </h2>
            <p className="text-xs text-muted-foreground">
              One tile per outlet. Click a tile to drill into its items. Use the toggle in the corner to mark an outlet active or inactive.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[11px] cursor-pointer" data-testid="landing-show-inactive-label">
              <input
                type="checkbox"
                checked={showInactiveUnits}
                onChange={(e) => setShowInactiveUnits(e.target.checked)}
                data-testid="landing-show-inactive-toggle"
                className="h-3 w-3"
              />
              <span className="text-muted-foreground">Show inactive units</span>
            </label>
            <Button asChild variant="secondary" size="sm" data-testid="tiles-open-reorder">
              <Link to="/tools/fnb-po/reorder">Company reorder view →</Link>
            </Button>
          </div>
        </div>
        <LocationHealthGrid
          tiles={tiles}
          loading={loading}
          onToggleActive={onToggleActive}
          saving={savingLocId}
        />
        <div className="pt-1">
          <Link
            to="/tools/fnb-po/bar-stock"
            className="text-xs text-foreground/60 hover:text-foreground underline underline-offset-4"
            data-testid="landing-bar-stock-link"
          >
            Bar Stock items (checked monthly at Kasol 2) →
          </Link>
        </div>
      </section>

      <section className="space-y-3" data-testid="master-data-section">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-wider text-foreground/80">
            Master Data
          </h2>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Static reference data. Refresh only when vendor rates or sales history change.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MasterDataMiniCard
            meta={{ key: "vendor_rates", label: "Vendor Rates & Item Master" }}
            entry={status.vendor_rates}
          />
          <MasterDataMiniCard
            meta={{ key: "sales", label: "Cafe Sales History" }}
            entry={status.sales}
          />
          <MasterDataMiniCard
            meta={{ key: "bom", label: "Bill of Materials (Recipes)" }}
            entry={status.bom}
          />
        </div>
      </section>
    </div>
  );
}
